'use client';

import {
  IngestionSourceType,
  PartitionType,
  type BucketPathPartitionSpec,
  type CreateIngestionPipelineInput,
  type GcsSourceProperties,
  type S3SourceProperties,
} from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { IngestionPipelineCreationFlow } from './IngestionPipelineCreationFlow';
import {
  defaultWizardState,
  mapSegments,
  type IngestionPipelineWizardState,
} from './IngestionPipelineWizardState';

interface IngestionPipelineWizardPanelProps {
  workspaceId: string;
  onAbort: () => void;
  onCreated: () => void;
}

export function IngestionPipelineWizardPanel({
  workspaceId,
  onAbort,
  onCreated,
}: IngestionPipelineWizardPanelProps) {
  const dal = useDAL();
  const { snackbar } = useWorkspace();

  const save = async (
    state: IngestionPipelineWizardState
  ): Promise<boolean> => {
    const bucketPathPartitionSpec: BucketPathPartitionSpec = {
      separator: state.partitionSpec.separator,
      segments: mapSegments(state.partitionSpec.segments),
    };

    let request: CreateIngestionPipelineInput;

    if (state.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3) {
      const s3SourceProperties: S3SourceProperties = {
        bucket: state.s3Bucket,
        prefix: '',
        region: state.s3Region,
        ownerAccountId: state.s3OwnerAccountId,
        fileFormat: state.fileFormat,
        partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
        bucketPathPartitionSpec,
      };

      request = {
        name: state.name,
        namespace: state.namespace,
        tableName: state.tableName,
        sourceType: state.sourceType,
        s3SourceProperties,
      };
    } else {
      const gcsSourceProperties: GcsSourceProperties = {
        bucket: state.gcsBucket,
        prefix: '',
        projectId: state.gcsProjectId,
        location: state.gcsLocation,
        fileFormat: state.fileFormat,
        partitionType: PartitionType.PARTITION_TYPE_BUCKET_PATH,
        bucketPathPartitionSpec,
      };

      request = {
        name: state.name,
        namespace: state.namespace,
        tableName: state.tableName,
        sourceType: state.sourceType,
        gcsSourceProperties,
      };
    }

    try {
      await dal.lakehouse.createIngestionPipeline(workspaceId, request);
      onCreated();
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create pipeline';
      snackbar.showErrorMessage(message);
      return false;
    }
  };

  return (
    <Wizard<IngestionPipelineWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={save}
      flow={[IngestionPipelineCreationFlow]}
      initialState={defaultWizardState}
      title='Create ingestion pipeline'
      stepperLabels={['Pipeline Details', 'Source', 'Partitioning', 'Review']}
    />
  );
}
